// contractHealth — aggregates counts + nearest-example subtitle from a
// contract's tracking surfaces (deliverables / milestones / insurance /
// variations) for the Details summary strip. Pure function: takes a
// snapshot of inputs (already fetched by React Query), no side effects.
// Mirrors the destination tabs' status semantics so a click-through
// lands on the same record set.

import { daysBetween } from "../../utils/isoDay";

export interface DeliverableSnapshot {
  id: string;
  name: string;
  status: string;
  dueDate: string | undefined;
  archived: boolean;
  /** ISO timestamp of first submission, when known. Required to
   *  distinguish derived `overdue` (no submission, supplier chase) from
   *  derived `late` (submitted after due date, customer review).
   *  Optional for backward compatibility — callers that omit it cannot
   *  flip a row to `late`, only to `overdue`. */
  submittedAt?: string | null;
}

export interface MilestoneSnapshot {
  id: string;
  name: string;
  /** True when the milestone has been delivered (deliveredAt or acceptedAt or paidAt is set). */
  isDelivered: boolean;
  plannedDate: string | undefined;
  archived: boolean;
}

export interface InsuranceSnapshot {
  id: string;
  name: string;
  expiryDate: string | undefined;
  archived: boolean;
}

export interface VariationSnapshot {
  id: string;
  name: string;
  status: string;
  createdAt: string;
  archived: boolean;
}

export interface ContractTrackingSnapshot {
  deliverables: DeliverableSnapshot[];
  milestones: MilestoneSnapshot[];
  insurance: InsuranceSnapshot[];
  variations: VariationSnapshot[];
  /** ISO date (YYYY-MM-DD) — usually getBrisbaneToday(). Injected so tests are deterministic. */
  today: string;
  /** Days-before-expiry the insurance tab considers "expiring". */
  insuranceReminderDays: number;
}

export interface NearestExample {
  id: string;
  name: string;
  daysOverdue?: number;
  daysUntilExpiry?: number;
  daysSinceProposed?: number;
  subtitle: string;
}

export interface CategoryHealth {
  count: number;
  nearestExample?: NearestExample;
}

export interface ContractHealth {
  overdueDeliverables: CategoryHealth;
  overdueMilestones: CategoryHealth;
  insuranceExpiring: CategoryHealth;
  pendingVariations: CategoryHealth;
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

// `overdue` here mirrors the derived `overdue` from
// `frontend/src/components/manage/tracking/deliverable/deriveDeliverableStatus.ts`
// and the backend `deriveDeliverableStatus` exactly:
// only `not-started` past due AND with no submission flips to overdue.
// Submitted-late rows are derived `late` (customer review queue) and
// counted toward the Awaiting review tile, not the Contract overview
// Overdue card.

function deliverableIsOverdue(
  d: DeliverableSnapshot,
  today: string,
): boolean {
  if (d.archived) return false;
  if (!d.dueDate) return false;
  if (d.dueDate >= today) return false;
  if (d.submittedAt) return false;
  if (d.status !== "not-started") return false;
  return true;
}

function milestoneIsOverdue(
  m: MilestoneSnapshot,
  today: string,
): boolean {
  if (m.archived) return false;
  if (!m.plannedDate) return false;
  if (m.isDelivered) return false;
  return m.plannedDate < today;
}

function insuranceIsExpiring(
  i: InsuranceSnapshot,
  today: string,
  reminderDays: number,
): boolean {
  if (i.archived) return false;
  if (!i.expiryDate) return false;
  if (i.expiryDate < today) return false;
  const cutoff = addDays(today, reminderDays);
  return i.expiryDate <= cutoff;
}

function variationIsPending(v: VariationSnapshot): boolean {
  if (v.archived) return false;
  return v.status === "proposed";
}

function formatOverdueSubtitle(name: string, daysOverdue: number): string {
  return `${name} · ${daysOverdue}d overdue`;
}

// Below this many days to expiry the subtitle counts down in days; at or
// above it, it rounds to whole weeks.
const EXPIRY_DAYS_SUBTITLE_CUTOFF = 14;
const DAYS_PER_WEEK = 7;

function formatInsuranceSubtitle(
  name: string,
  daysUntilExpiry: number,
): string {
  if (daysUntilExpiry < EXPIRY_DAYS_SUBTITLE_CUTOFF) {
    return `${name} · in ${daysUntilExpiry} ${daysUntilExpiry === 1 ? "day" : "days"}`;
  }
  const weeks = Math.round(daysUntilExpiry / DAYS_PER_WEEK);
  return `${name} · in ${weeks} ${weeks === 1 ? "week" : "weeks"}`;
}

function formatVariationSubtitle(
  name: string,
  daysSinceProposed: number,
): string {
  return `${name} · proposed ${daysSinceProposed} ${daysSinceProposed === 1 ? "day" : "days"} ago`;
}

export function computeContractHealth(
  snap: ContractTrackingSnapshot,
): ContractHealth {
  const today = snap.today;

  // Overdue deliverables
  const overdueDel = snap.deliverables
    .filter((d) => deliverableIsOverdue(d, today))
    .map((d) => ({
      d,
      daysOverdue: daysBetween(d.dueDate!, today),
    }))
    .sort((a, b) => b.daysOverdue - a.daysOverdue);

  // Overdue milestones
  const overdueMs = snap.milestones
    .filter((m) => milestoneIsOverdue(m, today))
    .map((m) => ({
      m,
      daysOverdue: daysBetween(m.plannedDate!, today),
    }))
    .sort((a, b) => b.daysOverdue - a.daysOverdue);

  // Expiring insurance
  const expiringIns = snap.insurance
    .filter((i) => insuranceIsExpiring(i, today, snap.insuranceReminderDays))
    .map((i) => ({
      i,
      daysUntilExpiry: daysBetween(today, i.expiryDate!),
    }))
    .sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);

  // Pending variations
  const pending = snap.variations
    .filter(variationIsPending)
    .map((v) => ({
      v,
      daysSinceProposed: daysBetween(v.createdAt.slice(0, 10), today),
    }))
    .sort((a, b) => b.daysSinceProposed - a.daysSinceProposed);

  return {
    overdueDeliverables: {
      count: overdueDel.length,
      nearestExample:
        overdueDel.length > 0
          ? {
              id: overdueDel[0].d.id,
              name: overdueDel[0].d.name,
              daysOverdue: overdueDel[0].daysOverdue,
              subtitle: formatOverdueSubtitle(
                overdueDel[0].d.name,
                overdueDel[0].daysOverdue,
              ),
            }
          : undefined,
    },
    overdueMilestones: {
      count: overdueMs.length,
      nearestExample:
        overdueMs.length > 0
          ? {
              id: overdueMs[0].m.id,
              name: overdueMs[0].m.name,
              daysOverdue: overdueMs[0].daysOverdue,
              subtitle: formatOverdueSubtitle(
                overdueMs[0].m.name,
                overdueMs[0].daysOverdue,
              ),
            }
          : undefined,
    },
    insuranceExpiring: {
      count: expiringIns.length,
      nearestExample:
        expiringIns.length > 0
          ? {
              id: expiringIns[0].i.id,
              name: expiringIns[0].i.name,
              daysUntilExpiry: expiringIns[0].daysUntilExpiry,
              subtitle: formatInsuranceSubtitle(
                expiringIns[0].i.name,
                expiringIns[0].daysUntilExpiry,
              ),
            }
          : undefined,
    },
    pendingVariations: {
      count: pending.length,
      nearestExample:
        pending.length > 0
          ? {
              id: pending[0].v.id,
              name: pending[0].v.name,
              daysSinceProposed: pending[0].daysSinceProposed,
              subtitle: formatVariationSubtitle(
                pending[0].v.name,
                pending[0].daysSinceProposed,
              ),
            }
          : undefined,
    },
  };
}
